import React, { useContext, useEffect, useState } from 'react'
import MessageContext from 'shared/components/context/message'
import FileInput from 'shared/components/inputs/file-input'
import styled from 'styled-components'
import { BUTTON_ACTIVE_OPACITY, MAX_FILE_DURATION, MAX_FILE_SIZE } from '../../../constants'
import { CreationToolsContext } from '../../../manager'
import CreationToolGAEvent from '../../../types/ga-event'
import CreationToolWelcomeButton from '../../../types/welcome-button'
import { WelcomeBlock } from '../../blocks'
import { NewIcon } from '../../icons'
import { Subtitle, Text } from '../../typography'
import { CreationToolsWelcomeContext } from '../context'

const Container = styled.div`
    padding: 6px;
    width: 100%;
`

const Content = styled(WelcomeBlock as any)`
    color: #fff;
    cursor: pointer;
    display: block;
    height: 100%;
    position: relative;
    text-align: center;

    &:active {
        opacity: ${BUTTON_ACTIVE_OPACITY};
    }

    &[data-dragover='true'] {
        background: rgba(236, 236, 236, 0.2);
    }

    input[type='file'] {
        cursor: pointer;
        height: 100%;
        left: 0;
        margin: 0;
        opacity: 0;
        padding: 0;
        position: absolute;
        top: 0;
        width: 100%;
    }
`

const IconWrapper = styled.div`
    align-items: center;
    display: flex;
    flex-direction: column;
    height: 50px;
    justify-content: center;
    margin: 0 auto 10px;
    width: 70px;
`

const New = styled(NewIcon)`
    position: absolute;
    right: 10px;
    top: 10px;
`

type Props = {
    className?: string
    button: CreationToolWelcomeButton
}

const CreationToolsWelcomeButton = ({ className, button }: Props) => {
    const { sendMessage } = useContext(MessageContext)
    const { setFile, trackEvent } = useContext(CreationToolsContext)
    const { isDraggingFile } = useContext(CreationToolsWelcomeContext)
    const [isDraggedOver, setIsDraggedOver] = useState<boolean>(false)
    const title = CreationToolWelcomeButton.title(button)
    const description = CreationToolWelcomeButton.description(button)
    const url = CreationToolWelcomeButton.url(button)
    const mimeTypes = CreationToolWelcomeButton.mimeTypes(button)
    const Icon = CreationToolWelcomeButton.icon(button)

    const onDragEnter = () => {
        setIsDraggedOver(true)
    }

    const onDragLeave = () => {
        setIsDraggedOver(false)
    }

    const onError = async (_: Event, message: string, files: FileList) => {
        trackEvent(CreationToolGAEvent.welcomeButtonCategory(button), 'Bad Format Error', files[0].type)
        sendMessage(message, 'error')
    }

    const onFileAccepted = async (files: FileList) => {
        const eventLabel = isDraggedOver ? 'Drag And Drop' : 'Browse Files'
        if (files && files.length) {
            const { file } = (files as any)[0]
            setFile(file, CreationToolWelcomeButton.outputType(button))
        }
        trackEvent(CreationToolGAEvent.welcomeButtonCategory(button), 'Input', eventLabel)
    }

    useEffect(() => {
        if (!isDraggingFile) {
            setIsDraggedOver(false)
        }
    }, [isDraggingFile])

    return (
        <Container className={className} style={isDraggingFile && mimeTypes.length > 0 ? { zIndex: 11 } : {}}>
            <Content
                {...{
                    as: url ? 'a' : 'div',
                    href: url || undefined,
                    onDragEnter,
                    onDragLeave,
                }}
                data-dragover={isDraggedOver && mimeTypes.length > 0}
            >
                <>
                    <IconWrapper>{Icon && <Icon />}</IconWrapper>
                    <Subtitle as="h3">{title}</Subtitle>
                    {description && <Text>{description}</Text>}
                </>
                {CreationToolWelcomeButton.isNew(button) && <New />}
                {mimeTypes.length > 0 && (
                    <FileInput
                        {...{
                            mimeTypes,
                            onError,
                            onFileAccepted,
                            maxDuration: MAX_FILE_DURATION,
                            maxFilesize: MAX_FILE_SIZE,
                        }}
                    />
                )}
            </Content>
        </Container>
    )
}

export default CreationToolsWelcomeButton
